import { createSlice } from '@reduxjs/toolkit';

// 'full-mixpedia' and 'feed' are gated only while planModel.MIXPEDIA_FREE_FOR_ALL
// is off (src/backend/planModel.js) — flip the switch there, never this list.
// src/backend/tierService.js mirrors this map.
const GOAT_FEATURES = {
    'loudness-match': 'Loudness Matching',
    'metadata-repair': 'Metadata Repair',
    'smart-organize': 'Smart Organize',
    'cue-points': 'Cues & Loops',
    'automix': 'Automix',
    'full-mixpedia': 'Full Mixpedia',
    'smart-recommendations': 'Smart Recommendations',
    'whats-next': 'Unlimited Set Planner',
    'export-mixpedia': 'Export Mixpedia Reports',
    'feed': 'For You',
    'notifications': 'Notifications',
    'cloud-backup': 'Cloud Backup',
};

const CACHE_KEY = 'tier:cache';
const CACHE_AT_KEY = 'tier:cachedAt';
// 7 days — long enough to survive offline trips, short enough that a revoked
// subscription re-syncs in reasonable time. Refresh runs whenever the app is
// online anyway, so this ceiling only matters for extended offline use.
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

const DEFAULT_STATE = {
    effectiveTier: 'free',
    reason: 'free',
    trialStartDate: null,
    trialEndDate: null,
    trialDaysRemaining: null,
    isTrialActive: false,
    isTrialExpired: false,
    isLoaded: false,
    showUpgradeModal: false,
    upgradeFeature: null,
    upgradeContext: null, // what the user was looking at when a wall opened it ({ moreCount } / { total } / { list })
};

// Read the currently-authenticated userId from the same sources authSlice uses
// (localStorage on Electron, mg_user cookie on web). Used to gate cache reuse:
// a cache written for user A must NEVER hydrate user B's session — that's how
// a previously-goat account's `reason: 'subscription'` can survive into a
// newly-expired user's state and hide the trial banner.
function readCurrentUserId() {
    try {
        if (typeof localStorage !== 'undefined') {
            // Electron stores the user under 'auth:user'. Capacitor / web-shim
            // (mobile) stores it under 'web-shim:dev-user' — and on those
            // surfaces 'auth:user' is NEVER written (authSlice.persistUser is a
            // no-op when IS_WEB) and the mg_user cookie lives on the mixgoat.com
            // domain, unreachable from the WebView origin. Without this key the
            // tier cache could never persist or hydrate on mobile, so an
            // offline GOAT user always fell back to 'free' (the flight-mode
            // bug). Check both.
            const raw = localStorage.getItem('auth:user') || localStorage.getItem('web-shim:dev-user');
            if (raw) {
                const u = JSON.parse(raw);
                if (u && u.id) return String(u.id);
            }
        }
        if (typeof document !== 'undefined') {
            const m = document.cookie.match(/(?:^|;\s*)mg_user=([^;]+)/);
            if (m) {
                const u = JSON.parse(atob(decodeURIComponent(m[1])));
                if (u && u.id) return String(u.id);
            }
        }
    } catch { /* ignore */ }
    return null;
}

function hydrateInitialState() {
    try {
        const raw = localStorage.getItem(CACHE_KEY);
        const at = parseInt(localStorage.getItem(CACHE_AT_KEY) || '0', 10);
        if (!raw || !at) return { ...DEFAULT_STATE };
        if (Date.now() - at > TTL_MS) {
            localStorage.removeItem(CACHE_KEY);
            localStorage.removeItem(CACHE_AT_KEY);
            return { ...DEFAULT_STATE };
        }
        const cached = JSON.parse(raw);
        // Cache is scoped to the userId it was written for. If the current
        // user differs (or the cache predates this guard), discard it — the
        // server fetch in initializeTier will repopulate with fresh data.
        const currentUserId = readCurrentUserId();
        if (!cached.userId || !currentUserId || cached.userId !== currentUserId) {
            localStorage.removeItem(CACHE_KEY);
            localStorage.removeItem(CACHE_AT_KEY);
            return { ...DEFAULT_STATE };
        }
        return {
            ...DEFAULT_STATE,
            effectiveTier: cached.effectiveTier || 'free',
            reason: cached.reason || 'free',
            trialStartDate: cached.trialStartDate ?? null,
            trialEndDate: cached.trialEndDate ?? null,
            trialDaysRemaining: cached.trialDaysRemaining ?? null,
            isTrialActive: Boolean(cached.isTrialActive),
            isTrialExpired: Boolean(cached.isTrialExpired),
            isLoaded: true,
        };
    } catch {
        return { ...DEFAULT_STATE };
    }
}

function persistTier(state) {
    try {
        const userId = readCurrentUserId();
        if (!userId) return; // no user → don't poison the cache
        const payload = {
            userId,
            effectiveTier: state.effectiveTier,
            reason: state.reason,
            trialStartDate: state.trialStartDate,
            trialEndDate: state.trialEndDate,
            trialDaysRemaining: state.trialDaysRemaining,
            isTrialActive: state.isTrialActive,
            isTrialExpired: state.isTrialExpired,
        };
        localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
        localStorage.setItem(CACHE_AT_KEY, String(Date.now()));
    } catch { /* quota / private mode — ignore */ }
}

function clearTierCache() {
    try {
        localStorage.removeItem(CACHE_KEY);
        localStorage.removeItem(CACHE_AT_KEY);
    } catch { /* ignore */ }
}

const tierSlice = createSlice({
    name: 'tier',
    initialState: hydrateInitialState(),
    reducers: {
        setTierInfo(state, action) {
            const { tier, reason, trialInfo } = action.payload;
            state.effectiveTier = tier;
            state.reason = reason;
            if (trialInfo) {
                state.trialStartDate = trialInfo.trialStartDate;
                state.trialEndDate = trialInfo.trialEndDate;
                state.trialDaysRemaining = trialInfo.daysRemaining;
                state.isTrialActive = trialInfo.isTrialActive;
                state.isTrialExpired = trialInfo.isTrialExpired;
            }
            state.isLoaded = true;
            persistTier(state);
        },
        clearTier(state) {
            state.effectiveTier = 'free';
            state.reason = 'free';
            state.trialStartDate = null;
            state.trialEndDate = null;
            state.trialDaysRemaining = null;
            state.isTrialActive = false;
            state.isTrialExpired = false;
            state.isLoaded = false;
            clearTierCache();
        },
        // Payload: a feature key string, or `{ feature, context }` where
        // `context` is the plain object a wall passes so the paywall headline
        // can name what the user was looking at (utils/paywall.js).
        showUpgradeModal(state, action) {
            const payload = action.payload;
            const isObject = !!payload && typeof payload === 'object';
            const context = isObject && payload.context && typeof payload.context === 'object' ? payload.context : null;
            state.showUpgradeModal = true;
            state.upgradeFeature = (isObject ? payload.feature : payload) ?? null; // feature key
            state.upgradeContext = context;
        },
        hideUpgradeModal(state) {
            state.showUpgradeModal = false;
            state.upgradeFeature = null;
            state.upgradeContext = null;
        },
    },
});

export const GOAT_FEATURES_MAP = GOAT_FEATURES;
export const { setTierInfo, clearTier, showUpgradeModal, hideUpgradeModal } = tierSlice.actions;
export default tierSlice.reducer;
